import { assertIntegerInRange } from "../errors.js";
import { ceilDiv, roundHalfUpBigInt } from "../money/round.js";
/** The kinds of source a breakdown lists, in the vocabulary of the plan (spec/05 §4.1). */
export const FUNDING_BREAKDOWN_KINDS = ['comp', 'pass', 'wallet', 'postpaid'];
/**
 * The sources in the order the session consumes them, each with the seconds it still funds. Sources that fund
 * nothing are left out. This is the one definition of the order (see the header); the server's plan is built from it.
 */
export function sessionFundingOrder(state) {
    const comp = minute('comp', state.compSeconds);
    const passes = state.passes.map((share) => ({
        kind: 'pass',
        seconds: share.seconds,
        name: share.name,
        holdingId: share.holdingId,
        expiresAt: share.expiresAt ?? null,
        windowClosesAt: share.windowClosesAt ?? null,
        rate: null,
        amount: null,
    }));
    const wallet = state.wallet == null ? [] : [money('wallet', state.wallet.seconds, state.wallet.rate)];
    const postpaid = state.postpaid == null ? [] : [money('postpaid', state.postpaid.seconds, state.postpaid.rate)];
    const ordered = [comp, ...(state.walletFirst ? [...wallet, ...passes] : [...passes, ...wallet]), ...postpaid];
    for (const [index, item] of ordered.entries())
        assertIntegerInRange(item.seconds, `items[${index}].seconds`, 0);
    return ordered.filter((item) => item.seconds > 0);
}
/** The breakdown of a session's time: the ordered sources, their total, and what follows. */
export function buildFundingBreakdown(state) {
    const items = sessionFundingOrder(state);
    return {
        items,
        totalSeconds: items.reduce((total, item) => total + item.seconds, 0),
        then: state.grace.available && state.grace.seconds > 0 ? { kind: 'grace', seconds: state.grace.seconds } : { kind: 'end' },
    };
}
/**
 * The breakdown `elapsedSeconds` after it was built: played seconds are taken from the front, the way the session
 * consumes them, and a money source's cost is re-priced for what is left. The kiosk counts down with this between
 * leases, so the lines always add up to the clock.
 */
export function drainFundingBreakdown(breakdown, elapsedSeconds) {
    assertIntegerInRange(elapsedSeconds, 'elapsedSeconds', 0);
    let left = elapsedSeconds;
    const items = [];
    for (const item of breakdown.items) {
        const used = Math.min(left, item.seconds);
        left -= used;
        const seconds = item.seconds - used;
        if (seconds <= 0)
            continue;
        items.push(used === 0 ? item : { ...item, seconds, amount: item.rate === null ? null : breakdownCharge(seconds, item.rate) });
    }
    return { items, totalSeconds: items.reduce((total, item) => total + item.seconds, 0), then: breakdown.then };
}
/** The breakdown as it travels (the contract's `FundingBreakdown`: a rate as money and its billing unit), in this shape. */
export function breakdownFromWire(wire) {
    return {
        items: wire.items.map((item) => ({
            kind: item.kind,
            seconds: item.seconds,
            name: item.name,
            holdingId: item.holdingId,
            expiresAt: item.expiresAt,
            windowClosesAt: item.windowClosesAt,
            rate: item.hourlyRate !== null && item.billingUnitSeconds != null ? { hourlyAmount: item.hourlyRate, billingUnitSeconds: item.billingUnitSeconds } : null,
            amount: item.amount,
        })),
        totalSeconds: wire.totalSeconds,
        then: wire.then.kind === 'grace' ? { kind: 'grace', seconds: wire.then.seconds } : { kind: 'end' },
    };
}
/**
 * What is left of a breakdown when the session has `remainingSeconds` of funded time left — the clock a screen already
 * shows — so its lines always add up to that clock: the difference is taken from the front (`drainFundingBreakdown`).
 * A line that cannot be re-priced (no billing unit from an older api) leaves the breakdown as it came.
 */
export function breakdownAtRemaining(wire, remainingSeconds) {
    const breakdown = breakdownFromWire(wire);
    const played = Math.max(0, breakdown.totalSeconds - Math.max(0, Math.floor(remainingSeconds)));
    try {
        return drainFundingBreakdown(breakdown, played);
    }
    catch {
        return breakdown;
    }
}
/** What `seconds` of play cost at `rate`: `paygSegmentCharge`'s formula, on the rounding primitives alone. */
export function breakdownCharge(seconds, rate) {
    assertIntegerInRange(seconds, 'seconds', 0);
    assertIntegerInRange(rate.billingUnitSeconds, 'billingUnitSeconds', 1);
    const billable = ceilDiv(seconds, rate.billingUnitSeconds) * rate.billingUnitSeconds;
    const amount = roundHalfUpBigInt(BigInt(rate.hourlyAmount.amount) * BigInt(billable), 3600n);
    return { amount: Number(amount), currency: rate.hourlyAmount.currency };
}
function minute(kind, seconds) {
    return { kind, seconds, name: null, holdingId: null, expiresAt: null, windowClosesAt: null, rate: null, amount: null };
}
function money(kind, seconds, rate) {
    return { kind, seconds, name: null, holdingId: null, expiresAt: null, windowClosesAt: null, rate, amount: seconds > 0 ? breakdownCharge(seconds, rate) : null };
}
//# sourceMappingURL=funding-breakdown.js.map